import React, { useState, useEffect } from 'react';
import './App.css';
import LandingPage from './components/LandingPage';
import LearningPage from './components/LearningPage';
import DSAProblems from './components/DSAProblems';
import ProblemSolvePage from './components/ProblemSolvePage';
import AuthForm from './components/AuthForm';
import Dashboard from './components/Dashboard';
import ProfilePage from './components/ProfilePage';
import SetUsername from './components/SetUsername';
import { useAuth } from './context/AuthContext';
import MySubmissions from './components/MySubmissions';
import SystemDesignPage from './components/SystemDesignPage';
import SystemDesignViewer from './components/SystemDesignViewer';
import AuthCallback from './components/AuthCallback';
import LogoutCallback from './components/LogoutCallback';
import MyBatches from './components/MyBatches';
import BatchDetails from './components/BatchDetails';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import ContactPage from './components/ContactPage';
import SupportPage from './components/SupportPage';
import DocumentationPage from './components/DocumentationPage';
import CareersPage from './components/CareersPage';

/**
 * Main application component that handles custom routing and global state management.
 * 
 * SimpleCode uses a custom `window.history` based routing system rather than `react-router-dom`
 * to maintain strict control over navigation and state preservation during test attempts.
 * 
 * @returns {JSX.Element} The rendered application component based on current URL path.
 */
function App() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const { isAuthenticated, needsUsername, isLoggingOut } = useAuth();

  // Listen for browser back/forward button clicks
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  /**
   * Navigation utility that pushes a new state to the browser history and updates 
   * the internal path state to trigger a React re-render.
   * 
   * @param {string} path - The target URL path to navigate to (e.g., '/dashboard').
   */
  const navigate = (path) => {
    window.history.pushState({}, '', path);
    // Extract path without query parameters or hash fragments for the router
    const pathname = path.split('?')[0].split('#')[0];
    setCurrentPath(pathname);
  };

  // Scroll to top on every route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPath]);

  /**
   * Parses the current path to extract the numerical problem ID.
   * Used for routing to specific DSA coding problems.
   * 
   * @returns {number|null} The extracted problem ID, or null if the path doesn't match.
   */
  const getProblemId = () => {
    const match = currentPath.match(/^\/problems\/(\d+)$/);
    return match ? parseInt(match[1]) : null;
  };

  /**
   * Parses the current path to extract the system design content ID.
   * 
   * @returns {number|null} The extracted content ID, or null if the path doesn't match.
   */
  const getSystemDesignContentId = () => {
    const match = currentPath.match(/^\/system-design\/view\/(\d+)$/);
    return match ? parseInt(match[1]) : null;
  };

  /**
   * Core routing logic. Evaluates the `currentPath` state against known application routes
   * and returns the appropriate page component. It also enforces authentication guards.
   * 
   * @returns {JSX.Element} The specific page component to render.
   */
  const renderCurrentRoute = () => {
    // Show logout transition screen
    if (isLoggingOut) {
      return <LogoutCallback />;
    }

    // Check for problem solve page first (more specific route)
    const problemId = getProblemId();
    if (problemId) {
      return <ProblemSolvePage navigate={navigate} questionId={problemId} />;
    }

    // Check for system design viewer page (more specific route)
    const systemDesignContentId = getSystemDesignContentId();
    if (systemDesignContentId) {
      return <SystemDesignViewer navigate={navigate} contentId={systemDesignContentId} />;
    }

    // Check for batch details component
    const batchId = currentPath.match(/^\/batches\/(\d+)$/);
    if (batchId) {
      return isAuthenticated
        ? <BatchDetails navigate={navigate} batchId={parseInt(batchId[1])} />
        : <AuthForm navigate={navigate} />;
    }

    // Handle other routes
    switch (currentPath) {
      case '/':
        return <LandingPage navigate={navigate} />;
      case '/batches':
        return isAuthenticated
          ? (needsUsername ? <SetUsername navigate={navigate} /> : <MyBatches navigate={navigate} />)
          : <AuthForm navigate={navigate} />;
      case '/dashboard':
        return isAuthenticated
          ? (needsUsername ? <SetUsername navigate={navigate} /> : <Dashboard navigate={navigate} />)
          : <LandingPage navigate={navigate} />;
      case '/learning':
        return isAuthenticated
          ? (needsUsername ? <SetUsername navigate={navigate} /> : <LearningPage navigate={navigate} />)
          : <LearningPage navigate={navigate} />;
      case '/dsa-problems':
        return <DSAProblems navigate={navigate} />;
      case '/system-design':
        return <SystemDesignPage navigate={navigate} />;
      case '/profile':
        return isAuthenticated
          ? (needsUsername ? <SetUsername navigate={navigate} /> : <ProfilePage navigate={navigate} />)
          : <LandingPage navigate={navigate} />;
      case '/signin':
        return isAuthenticated
          ? (needsUsername ? <SetUsername navigate={navigate} /> : <Dashboard navigate={navigate} />)
          : <AuthForm navigate={navigate} />;
      case '/set-username':
        return isAuthenticated && needsUsername
          ? <SetUsername navigate={navigate} />
          : (isAuthenticated ? <Dashboard navigate={navigate} /> : <AuthForm navigate={navigate} />);
      case '/submissions':
        return isAuthenticated
          ? (needsUsername ? <SetUsername navigate={navigate} /> : <MySubmissions navigate={navigate} />)
          : <AuthForm navigate={navigate} />;
      case '/privacy-policy':
        return <PrivacyPolicyPage navigate={navigate} />;
      case '/terms-of-service':
        return <TermsOfServicePage navigate={navigate} />;
      case '/contact':
        return <ContactPage navigate={navigate} />;
      case '/support':
        return <SupportPage navigate={navigate} />;
      case '/documentation':
        return <DocumentationPage navigate={navigate} />;
      case '/careers':
        return <CareersPage navigate={navigate} />;
      case '/auth/callback':
        return isAuthenticated
          ? (needsUsername ? <SetUsername navigate={navigate} /> : <Dashboard navigate={navigate} />)
          : <AuthCallback />;
      default:
        return isAuthenticated
          ? (needsUsername ? <SetUsername navigate={navigate} /> : <Dashboard navigate={navigate} />)
          : <LandingPage navigate={navigate} />;
    }
  };

  return (
    <div className="App">
      {renderCurrentRoute()}
    </div>
  );
}

export default App;